import React, { useState, useMemo } from 'react';
import { SurveyTemplate } from '../../types/survey.types';
import { extractTemplateVariables, getVariableLocations } from '../../utils/templateVariables';
import { stripHTML } from '../../utils/stripHTML';

interface TemplatePreviewModalProps {
  template: SurveyTemplate;
  onClose: () => void;
  onUseTemplate: (template: SurveyTemplate) => void;
}

const questionTypeLabels: Record<string, string> = {
  text: 'Text',
  textarea: 'Multi-line Text',
  number: 'Number',
  email: 'Email',
  phone: 'Phone',
  date: 'Date',
  radio: 'Single Choice',
  checkbox: 'Multiple Choice',
  dropdown: 'Dropdown',
  multiselect: 'Multi-Select',
  rating: 'Rating',
  scale: 'Likert Scale',
  yesno: 'Yes/No',
  file: 'File Upload',
  signature: 'Signature',
  matrix: 'Matrix',
  ranking: 'Ranking',
};

const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  template,
  onClose,
  onUseTemplate,
}) => {
  const variableNames = useMemo(() => extractTemplateVariables(template), [template]);
  const variableLocs = useMemo(() => getVariableLocations(template), [template]);

  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(template.sections.map(s => s.id))
  );

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full my-8 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-bold" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-heading)' }}>
              {template.name}
            </h3>
            {template.description && (
              <p className="text-sm mt-1" style={{ color: 'var(--color-muted)' }}>
                {stripHTML(template.description)}
              </p>
            )}
            <div className="flex items-center gap-3 mt-2">
              <span className="text-xs font-medium" style={{ color: 'var(--color-muted)' }}>
                {template.sectionCount} sections
              </span>
              <span className="text-xs font-medium" style={{ color: 'var(--color-muted)' }}>
                {template.questionCount} questions
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg flex-shrink-0"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Template Variables */}
        {variableNames.length > 0 && (
          <div
            className="mb-4 p-3 rounded-xl border"
            style={{ borderColor: 'var(--color-accent)', backgroundColor: 'var(--primary-50)' }}
          >
            <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--color-primary)' }}>
              Template Variables ({variableNames.length})
            </h4>
            <p className="text-xs mb-2" style={{ color: 'var(--color-muted)' }}>
              These placeholders will be filled in when creating a survey from this template.
            </p>
            <div className="space-y-1">
              {variableNames.map(v => (
                <div key={v} className="flex items-center justify-between text-xs">
                  <code
                    className="px-1.5 py-0.5 rounded bg-white font-mono"
                    style={{ color: 'var(--color-accent)' }}
                  >
                    {`{{${v}}}`}
                  </code>
                  <span style={{ color: 'var(--color-muted)' }}>
                    {variableLocs[v]?.length || 0} {(variableLocs[v]?.length || 0) === 1 ? 'use' : 'uses'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sections */}
        <div className="space-y-3 mb-6">
          {template.sections.map((section, sIdx) => (
            <div
              key={section.id}
              className="border rounded-xl overflow-hidden"
              style={{ borderColor: 'var(--glass-border-light)' }}
            >
              {/* Section header */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: 'var(--primary-50)', color: 'var(--color-primary)' }}
                  >
                    {sIdx + 1}
                  </span>
                  <span className="font-medium text-sm" style={{ color: 'var(--color-primary)' }}>
                    {section.title}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
                    ({section.questions.length} {section.questions.length === 1 ? 'question' : 'questions'})
                  </span>
                </div>
                <svg
                  className={`w-4 h-4 transition-transform ${expandedSections.has(section.id) ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ color: 'var(--color-muted)' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Questions list */}
              {expandedSections.has(section.id) && (
                <div className="border-t px-4 py-2" style={{ borderColor: 'var(--glass-border-light)' }}>
                  {section.questions.map((question, qIdx) => (
                    <div
                      key={question.id}
                      className="flex items-start gap-3 py-2"
                      style={{ borderBottom: qIdx < section.questions.length - 1 ? '1px solid var(--glass-border-light)' : 'none' }}
                    >
                      <span
                        className="text-xs font-medium mt-0.5 flex-shrink-0"
                        style={{ color: 'var(--color-muted)', minWidth: '20px' }}
                      >
                        {qIdx + 1}.
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm" style={{ color: 'var(--color-primary)' }}>
                          {question.title}
                          {question.required && (
                            <span style={{ color: 'var(--status-error)' }}> *</span>
                          )}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className="text-xs px-1.5 py-0.5 rounded"
                            style={{
                              backgroundColor: 'var(--primary-50)',
                              color: 'var(--color-accent)',
                            }}
                          >
                            {questionTypeLabels[question.type] || question.type}
                          </span>
                          {question.options && question.options.length > 0 && (
                            <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
                              {question.options.length} options
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer actions */}
        <div className="flex gap-3 justify-end pt-4 border-t" style={{ borderColor: 'var(--glass-border-light)' }}>
          <button
            onClick={onClose}
            className="glass-btn-secondary glass-btn-sm"
          >
            Close
          </button>
          <button
            onClick={() => onUseTemplate(template)}
            className="glass-btn-primary glass-btn-sm"
          >
            Use This Template
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewModal;
